import { isValidElement, useId } from 'react';

import { styled } from '@mui/material';

import { Checkbox } from '../Checkbox/Checkbox';
import { InfoButton } from '../InfoButton';
import { Status } from '../Status';
import { checkboxItemClasses } from './theme';

import type { ReactElement } from 'react';
import type { InfoButtonProps } from '../InfoButton';
import type { StatusProps } from '../Status';
import type { CheckboxItemOwnerState, CheckboxItemProps } from './types';

export function CheckboxItem(props: CheckboxItemProps) {
  const {
    checked,
    className,
    defaultChecked,
    disabled,
    error,
    id: idProp,
    indeterminate,
    info: infoProp,
    inputRef,
    name,
    onChange,
    onClick,
    readOnly,
    ref,
    slotProps,
    status: statusProp,
    style,
    text,
    ...others
  } = props;

  const { checkbox: checkboxSlotProps } = slotProps ?? {};

  const ownerState: CheckboxItemOwnerState = props;

  const fallbackId = useId();
  const id = idProp ?? fallbackId;

  let status = statusProp;
  if (isValidElement(statusProp) && statusProp.type === Status) {
    const element = statusProp as ReactElement<StatusProps>;
    status = (
      <CheckboxItemStatus
        {...element.props}
        size={element.props.size ?? 'sm'}
        ownerState={ownerState}
      />
    );
  }

  let info = infoProp;
  if (isValidElement(infoProp) && infoProp.type === InfoButton) {
    const element = infoProp as ReactElement<InfoButtonProps>;
    info = (
      <CheckboxItemInfoButton
        {...element.props}
        size={element.props.size ?? 'md'}
        color={element.props.color ?? 'default'}
        ownerState={ownerState}
      />
    );
  }

  return (
    <CheckboxItemRoot
      className={className}
      onClick={onClick}
      ref={ref}
      style={style}
      ownerState={ownerState}
      {...others}
    >
      <CheckboxItemCheckbox
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        error={error}
        id={id}
        indeterminate={indeterminate}
        inputRef={inputRef}
        name={name}
        onChange={onChange}
        readOnly={readOnly}
        ownerState={ownerState}
        {...checkboxSlotProps}
      />
      {(text || status) && (
        <CheckboxItemLabel htmlFor={id} ownerState={ownerState}>
          {text && <CheckboxItemText ownerState={ownerState}>{text}</CheckboxItemText>}
          {status}
        </CheckboxItemLabel>
      )}
      {info}
    </CheckboxItemRoot>
  );
}

const CheckboxItemRoot = styled('div', {
  target: checkboxItemClasses.root,
})<{ ownerState: CheckboxItemOwnerState }>`
  display: inline-flex;
  align-items: center;
`;

const CheckboxItemCheckbox = styled(Checkbox, {
  target: checkboxItemClasses.checkbox,
})<{ ownerState: CheckboxItemOwnerState }>`
  margin: 8px 8px 8px 0px;
`;

const CheckboxItemLabel = styled('label', {
  target: checkboxItemClasses.label,
})<{ ownerState: CheckboxItemOwnerState }>`
  min-height: 21px;
  display: inline-flex;
  align-items: center;
  cursor: ${({ ownerState }) => (ownerState.disabled ? 'initial' : 'pointer')};
`;

const CheckboxItemText = styled('span', {
  target: checkboxItemClasses.text,
})<{ ownerState: CheckboxItemOwnerState }>`
  ${({ theme }) => theme.typography['body/normal/md']};
  color: ${({ theme, ownerState }) =>
    ownerState.disabled ? theme.color['text/disabled'] : theme.color['text/default']};
`;

const CheckboxItemStatus = styled(Status, {
  target: checkboxItemClasses.status,
})<{ ownerState: CheckboxItemOwnerState }>`
  .${checkboxItemClasses.text} + & {
    margin-left: 4px;
  }
`;

const CheckboxItemInfoButton = styled(InfoButton, {
  target: checkboxItemClasses.infoButton,
})<{ ownerState: CheckboxItemOwnerState }>`
  color: ${({ theme }) => theme.color['icon/weaker/default']};

  .${checkboxItemClasses.label} + & {
    margin-left: 4px;
  }
`;
